import { useIntl } from 'react-intl';

import { BulkImportUserToogleActionName } from '~/enums/BulkImportUserToogleActionName';

type BulkImportUserAction = Record<BulkImportUserToogleActionName, boolean>;

type WarningModalProps = {
  actionButtonText: string;
  actionButtonVariant: 'primary' | 'red';
  warningMessage: string;
  warningTitle: string;
  requireConfirm?: boolean;
  warningMessageToConfirm?: string;
  textToConfirm?: string | boolean;
};

export const useBulkImportWarningModal = () => {
  const intl = useIntl();

  const confirmationTextToDeleteUsers = intl.formatMessage({
    id: 'BulkImportWarningModal.deleteConfirmationText',
    defaultMessage: 'DELETE',
    description: 'Text user must type to confirm deletion',
  });

  const warningMessageToDeleteUsers = intl.formatMessage({
    id: 'BulkImportWarningModal.deleteConfirmationPrompt',
    defaultMessage: 'Please type "DELETE" to confirm you want to proceed',
    description: 'Prompt asking user to type DELETE to confirm',
  });

  const usersUpdateWarningTitle = intl.formatMessage({
    id: 'BulkImportWarningModal.updateWarningTitle',
    defaultMessage: 'Are you sure you want to update your user list?',
    description: 'Warning title for updating user list',
  });

  const usersUpdateWarningMessage = intl.formatMessage({
    id: 'BulkImportWarningModal.updateWarningMessage',
    defaultMessage:
      'By enabling these options, your user list will be updated according to the options. This action is permanent and cannot be undone. Are you sure you want to continue?',
    description: 'Warning message for updating user list',
  });

  const usersDeleteWarningTitle = intl.formatMessage({
    id: 'BulkImportWarningModal.deleteWarningTitle',
    defaultMessage: 'Permanently delete missing users?',
    description: 'Warning title for deleting missing users',
  });

  const usersDeleteWarningMessage = intl.formatMessage({
    id: 'BulkImportWarningModal.deleteWarningMessage',
    defaultMessage:
      'By enabling this option, all users currently in the platform, but not in the uploaded spreadsheet will be removed. This action is permanent and cannot be undone.',
    description: 'Warning message for deleting missing users',
  });

  const usersUpdateAndDeleteWarningTitle = intl.formatMessage({
    id: 'BulkImportWarningModal.updateAndDeleteWarningTitle',
    defaultMessage:
      'Permanently delete missing users and update existing users?',
    description: 'Warning title for both updating and deleting users',
  });

  const usersUpdateAndDeleteWarningMessage = intl.formatMessage({
    id: 'BulkImportWarningModal.updateAndDeleteWarningMessage',
    defaultMessage:
      'By enabling these options, all users currently in the platform, but not in the uploaded spreadsheet will be removed. Users will also be updated with the newest list imported. This action is permanent and cannot be undone.',
    description: 'Warning message for both updating and deleting users',
  });

  const warningModalBasePropsMap: Record<
    'updateUsers' | 'deleteUsers' | 'deleteAndUpdateUsers',
    WarningModalProps
  > = {
    updateUsers: {
      actionButtonText: intl.formatMessage({
        id: 'BulkImportWarningModal.continueToImport',
        defaultMessage: 'Continue to Import',
        description: 'Button text to continue with import',
      }),
      actionButtonVariant: 'primary',
      warningMessage: usersUpdateWarningMessage,
      warningTitle: usersUpdateWarningTitle,
      textToConfirm: false,
    },
    deleteUsers: {
      actionButtonText: intl.formatMessage({
        id: 'BulkImportWarningModal.deletePermanently',
        defaultMessage: 'Delete Permanently',
        description: 'Button text to permanently delete users',
      }),
      actionButtonVariant: 'red',
      warningMessage: usersDeleteWarningMessage,
      warningTitle: usersDeleteWarningTitle,
      requireConfirm: true,
      warningMessageToConfirm: warningMessageToDeleteUsers,
      textToConfirm: confirmationTextToDeleteUsers,
    },
    deleteAndUpdateUsers: {
      actionButtonText: intl.formatMessage({
        id: 'BulkImportWarningModal.deletePermanently',
        defaultMessage: 'Delete Permanently',
        description: 'Button text to permanently delete users',
      }),
      actionButtonVariant: 'red',
      warningMessage: usersUpdateAndDeleteWarningMessage,
      warningTitle: usersUpdateAndDeleteWarningTitle,
      requireConfirm: true,
      warningMessageToConfirm: warningMessageToDeleteUsers,
      textToConfirm: confirmationTextToDeleteUsers,
    },
  };

  const getBaseWarningModalProps = (
    importActions: BulkImportUserAction,
  ): WarningModalProps => {
    const { deleteMissingUsers, updateExistingUsers } = importActions;
    if (deleteMissingUsers && updateExistingUsers) {
      return warningModalBasePropsMap.deleteAndUpdateUsers;
    }
    if (deleteMissingUsers) {
      return warningModalBasePropsMap.deleteUsers;
    }

    return warningModalBasePropsMap.updateUsers;
  };

  return { getBaseWarningModalProps };
};
